import React, { createContext, useContext, useReducer } from 'react';

interface User {
  id: string;
  name: string;
  avatar: string;
  points: number;
  level: number;
  xp: number;
  nextLevelXp: number;
}

interface UserState {
  currentUser: User;
  notifications: Notification[];
}

interface Notification {
  id: string;
  type: 'like' | 'comment' | 'follow' | 'reward' | 'level_up' | 'video' | 'song';
  message: string;
  timestamp: number;
  read: boolean;
  link?: string;
  data?: Record<string, unknown>;
}

type UserAction =
  | { type: 'UPDATE_POINTS'; payload: number }
  | { type: 'ADD_NOTIFICATION'; payload: Notification }
  | { type: 'MARK_NOTIFICATION_READ'; payload: string }
  | { type: 'ADD_XP'; payload: number };

const calculateNextLevelXp = (level: number) => {
  // Each level requires 20% more XP than the previous level
  return Math.floor(1000 * Math.pow(1.2, level - 1));
};

const initialState: UserState = {
  currentUser: {
    id: 'current-user',
    name: 'Current User',
    avatar: 'https://images.unsplash.com/photo-1535713875002-d1d0cf377fde?w=100&q=80',
    points: 750,
    level: 1,
    xp: 0,
    nextLevelXp: calculateNextLevelXp(1)
  },
  notifications: []
};

const userReducer = (state: UserState, action: UserAction): UserState => {
  switch (action.type) {
    case 'UPDATE_POINTS':
      return {
        ...state,
        currentUser: {
          ...state.currentUser,
          points: state.currentUser.points + action.payload
        }
      };
    case 'ADD_NOTIFICATION':
      return {
        ...state,
        notifications: [action.payload, ...state.notifications]
      };
    case 'MARK_NOTIFICATION_READ':
      return {
        ...state,
        notifications: state.notifications.map(notif =>
          notif.id === action.payload ? { ...notif, read: true } : notif
        )
      };
    case 'ADD_XP': {
      const newXp = state.currentUser.xp + action.payload;
      let { level, nextLevelXp } = state.currentUser;
      const notifications = [...state.notifications];

      // Check for level up
      while (newXp >= nextLevelXp) {
        level++;
        nextLevelXp = calculateNextLevelXp(level);
        notifications.unshift({
          id: Date.now().toString(),
          type: 'level_up',
          message: `Congratulations! You've reached Level ${level}!`,
          timestamp: Date.now(),
          read: false
        });
      }

      return {
        ...state,
        currentUser: {
          ...state.currentUser,
          xp: newXp,
          level,
          nextLevelXp
        },
        notifications
      };
    }
    default:
      return state;
  }
};

const UserContext = createContext<{
  state: UserState;
  dispatch: React.Dispatch<UserAction>;
} | null>(null);

export const UserProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [state, dispatch] = useReducer(userReducer, initialState);

  return (
    <UserContext.Provider value={{ state, dispatch }}>
      {children}
    </UserContext.Provider>
  );
};

export const useUser = () => {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return context;
};

export type { Notification };